import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  Button,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import callService from '../services/CallService';

export default function IncomingCallNotification({ 
  incomingCall, 
  onClear,
  serviceId,
  phoneNumberId 
}) {
  const [callState, setCallState] = useState('ringing'); // ringing, connecting, connected, rejected, ended
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Format duration (seconds to MM:SS)
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start timer when call is connected
  const startTimer = () => {
    let seconds = 0;
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    durationIntervalRef.current = setInterval(() => {
      seconds++;
      setCallDuration(seconds);
    }, 1000);
  };

  const stopTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  // Cleanup WebRTC
  const cleanupWebRTC = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    stopTimer();
  };

  // Create SDP answer for WebRTC
//   const createSdpAnswer = async () => {
//     try {
//       const pc = new RTCPeerConnection({
//         iceServers: [
//           { urls: 'stun:stun.l.google.com:19302' },
//           { urls: 'stun:stun1.l.google.com:19302' }
//         ]
//       });
//       peerConnectionRef.current = pc;

//       // Get microphone for agent
//       const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
//       localStreamRef.current = stream;
//       stream.getTracks().forEach(track => pc.addTrack(track, stream));

//       // Play incoming audio from caller
//       pc.ontrack = (event) => {
//         const audio = new Audio();
//         audio.srcObject = event.streams[0];
//         audio.play().catch(e => console.error('Audio error:', e));
//       };

//       // Create SDP answer
//       const answer = await pc.createAnswer();
//       await pc.setLocalDescription(answer);
      
//       return answer.sdp;
//     } catch (error) {
//       console.error('Create SDP answer error:', error);
//       throw error;
//     }
//   };
  const waitForIceGathering = (pc, timeoutMs = 3000) => new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timeout = setTimeout(resolve, timeoutMs);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timeout);
        resolve();
      }
    });
  });

  const createSdpAnswer = async (sdpOffer) => {
    if (!sdpOffer) {
      throw new Error('Missing caller SDP offer');
    }

    try {
      const configuration = {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
        ],
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      };

      const pc = new RTCPeerConnection(configuration);
      peerConnectionRef.current = pc;

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          throw new Error('Microphone access denied. Please allow microphone access.');
        }
        if (err.name === 'NotFoundError') {
          throw new Error('No microphone found. Please connect a microphone.');
        }
        throw new Error(`Could not access microphone: ${err.message}`);
      }

      localStreamRef.current = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          setError('Network connection failed. Please check your internet.');
          cleanupWebRTC();
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') {
          setError('Call connection failed');
          cleanupWebRTC();
        }
      };

      pc.ontrack = (event) => {
        const audio = new Audio();
        audio.srcObject = event.streams[0];
        audio.autoplay = true;
        setTimeout(() => {
          audio.play().catch((e) => console.error('Audio play error:', e));
        }, 100);
      };

      await pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });

      const answer = await Promise.race([
        pc.createAnswer(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout creating answer')), 10000)),
      ]);

      await pc.setLocalDescription(answer);
      await waitForIceGathering(pc);

      return pc.localDescription?.sdp || answer.sdp;
    } catch (error) {
      console.error('Create SDP answer error:', error);
      setError(error.message || 'Failed to setup call');
      throw error;
    }
  };
  // Toggle mute/unmute
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  // Accept call
  const handleAccept = async () => {
    try {
      setCallState('connecting');
      
      const sdpAnswer = await createSdpAnswer(incomingCall.sdp_offer);
      
      // Pre-accept (faster connection)
      await callService.preAcceptCall(phoneNumberId, incomingCall.call_id, sdpAnswer);
      
      // Accept call
      await callService.acceptCall(phoneNumberId, incomingCall.call_id, sdpAnswer);
      
      setCallState('connected');
      startTimer();
      
    } catch (error) {
      console.error('Accept call error:', error);
      setError(error.response?.data?.error?.message || 'Failed to accept call');
      setCallState('ringing');
      cleanupWebRTC();
    }
  };

  // Reject call
  const handleReject = async () => {
    try {
      setCallState('rejected');
      await callService.rejectCall(phoneNumberId, incomingCall.call_id);
      
      setTimeout(() => {
        onClear();
        cleanupWebRTC();
      }, 1500);
      
    } catch (error) {
      console.error('Reject call error:', error);
      setError(error.response?.data?.error?.message || 'Failed to reject call');
      setCallState('ringing');
    }
  };

  // End active call
  const handleEndCall = async () => {
    try {
      await callService.terminateCall(serviceId, incomingCall.call_id);
      setCallState('ended');
      cleanupWebRTC();
      
      setTimeout(() => {
        onClear();
      }, 1000);
    } catch (error) {
      console.error('End call error:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupWebRTC();
    };
  }, []);

  if (!incomingCall) return null;

  const callerName = incomingCall.contact_name || 'Unknown Caller';
  const callerNumber = incomingCall.contact_phone || 'Unknown Number';

  return (
    <Dialog 
      open={true} 
      onClose={callState === 'ringing' ? handleReject : handleEndCall}
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: 320,
          maxWidth: 400,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header with gradient background */}
      <Box sx={{ 
        p: 4, 
        textAlign: 'center',
        background: callState === 'connected' 
          ? 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Avatar 
          sx={{ 
            width: 80, 
            height: 80, 
            mx: 'auto', 
            mb: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white'
          }}
        >
          {callState === 'ringing' && <CallIcon sx={{ fontSize: 40 }} />}
          {callState === 'connecting' && <CircularProgress size={40} sx={{ color: 'white' }} />}
          {callState === 'connected' && <CallIcon sx={{ fontSize: 40 }} />}
          {callState === 'rejected' && <CallEndIcon sx={{ fontSize: 40 }} />}
          {callState === 'ended' && <CallEndIcon sx={{ fontSize: 40 }} />}
        </Avatar>
        
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          {callState === 'ringing' && 'Incoming Call'}
          {callState === 'connecting' && 'Connecting...'}
          {callState === 'connected' && 'Call in Progress'}
          {callState === 'rejected' && 'Call Rejected'}
          {callState === 'ended' && 'Call Ended'}
        </Typography>
        
        {(callState === 'ringing' || callState === 'connected') && (
          <>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {callerName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {callerNumber}
            </Typography>
          </>
        )}
        
        {callState === 'connected' && (
          <Typography variant="h3" sx={{ mt: 2, fontFamily: 'monospace' }}>
            {formatDuration(callDuration)}
          </Typography>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(0,0,0,0.1)' }}>
            {error}
          </Alert>
        )}
      </Box>
      
      {/* Action Buttons */}
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', gap: 3, bgcolor: '#f5f5f5' }}>
        
        {callState === 'ringing' && (
          <>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<CallIcon />}
              onClick={handleAccept}
              sx={{ 
                borderRadius: 8,
                px: 4,
                py: 1.5,
                minWidth: 120
              }}
            >
              Accept
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<CallEndIcon />}
              onClick={handleReject}
              sx={{ 
                borderRadius: 8,
                px: 4,
                py: 1.5,
                minWidth: 120
              }}
            >
              Reject
            </Button>
          </>
        )}
        
        {callState === 'connecting' && (
          <Button 
            variant="outlined" 
            color="error" 
            onClick={handleReject}
            sx={{ borderRadius: 8, px: 4 }}
          >
            Cancel
          </Button>
        )}
        
        {callState === 'connected' && (
          <>
            <Button 
              variant="outlined" 
              color="inherit"
              startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
              onClick={toggleMute}
              sx={{ borderRadius: 8, px: 3 }}
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<CallEndIcon />}
              onClick={handleEndCall}
              sx={{ 
                borderRadius: 8,
                px: 4,
                py: 1.5
              }}
            >
              End Call
            </Button>
          </>
        )}
        
        {(callState === 'rejected' || callState === 'ended') && (
          <Button 
            variant="contained" 
            onClick={onClear}
            sx={{ borderRadius: 8, px: 4 }}
          >
            Close
          </Button>
        )}
      </Box>
      
      {/* Progress bar for ringing state */}
      {callState === 'ringing' && (
        <Box sx={{ 
          height: 3, 
          bgcolor: '#ff9800',
          width: '100%',
          animation: 'progress 30s linear forwards'
        }} />
      )}
    </Dialog>
  );
}